/** XeAgents.ai**/
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { formatDate } from "@/lib/date-format";

// Get tenant base domain from environment (dev: voiceprodev.xenonlabs.ai, prod: xevoicepro.xenonlabs.ai)
const TENANT_BASE_DOMAIN = (() => {
  const apiBase = import.meta.env.VITE_API_BASE || '';
  // Extract domain from VITE_API_BASE (e.g., https://voiceprodev.xenonlabs.ai -> voiceprodev.xenonlabs.ai)
  const match = apiBase.match(/https?:\/\/([^/]+)/);
  return match ? match[1] : 'xevoicepro.xenonlabs.ai';
})();
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Building2, Check, AlertCircle, ArrowRight, Mail, CreditCard, RefreshCw, ExternalLink, Copy, Clock, Percent, Gift, Key } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import SetupPhasesManager, { SetupPhase } from "./SetupPhasesManager";

interface EnterpriseFormData {
  name: string;
  email: string;
  organizationName: string;
  country: string;
  currency: "INR" | "USD";
  planAmount: string;
  recurringMonthlyAmount: string;
  perMinuteRate: string;
  includedMinutes: string;
  maxAgents: string;
  maxCampaigns: string;
  maxKnowledgeBases: string;
  maxFlows: string;
  maxPhoneNumbers: string;
  notes: string;
  discountPercentage: string;
  setupPhases: SetupPhase[];
  // Tenant branding
  primaryColor: string;
  tagline: string;
  // Free trial options
  enableFreeTrial: boolean;
  freeTrialDays: string;
  freeTrialCredits: string;
}

interface EnterpriseOnboardResult {
  success: boolean;
  paymentLink?: string;
  emailSent: boolean;
  emailError?: string;
  // DNS provisioning
  dnsProvisioned?: boolean;
  dnsError?: string;
  // Free trial fields
  isFreeTrial?: boolean;
  trialExpiresAt?: string;
  trialCredits?: number;
  temporaryPassword?: string;
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    isActive: boolean;
  };
  organization: {
    id: string;
    name: string;
    slug?: string;
    subdomain?: string;
  };
}

interface EnterpriseAccount {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  organizationName: string;
  planAmount: string;
  originalAmount?: string;
  perMinuteRate: string;
  includedMinutes: number;
  discountPercentage: number;
  currency: string;
  paymentStatus: "pending" | "paid" | "expired";
  paymentLink: string;
  createdAt: string;
  paidAt?: string;
}

// Pricing tiers for reference - India (INR)
const PRICING_TIERS_IN = [
  { name: "Starter", price: 9999, perMin: 5.50, minutes: 1800, margin: "45%" },
  { name: "Growth", price: 16999, perMin: 5.25, minutes: 3200, margin: "43%" },
  { name: "Scale", price: 29999, perMin: 5.00, minutes: 6000, margin: "40%" },
  { name: "Custom", price: 50000, perMin: 4.75, minutes: 10500, margin: "37%" },
];

// Pricing tiers for reference - US/International (USD)
const PRICING_TIERS_US = [
  { name: "Starter", price: 299, perMin: 0.12, minutes: 2500, margin: "45%" },
  { name: "Growth", price: 499, perMin: 0.10, minutes: 5000, margin: "43%" },
  { name: "Scale", price: 999, perMin: 0.08, minutes: 12500, margin: "40%" },
  { name: "Custom", price: 2000, perMin: 0.07, minutes: 28500, margin: "37%" },
];

const MIN_ENTERPRISE_AMOUNT_IN = 39999; // Minimum ₹39,999
const MIN_ENTERPRISE_AMOUNT_US = 2000; // Minimum $2,000

export default function EnterpriseOnboarding() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [createdResult, setCreatedResult] = useState<EnterpriseOnboardResult | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string>("US");

  // Get defaults based on selected country
  const getDefaultFormData = (country: string, currencyOverride?: "INR" | "USD"): EnterpriseFormData => {
    const currency = currencyOverride ?? (country === "IN" ? "INR" : "USD");
    if (currency === "INR") {
      return {
        name: "",
        email: "",
        organizationName: "",
        country,
        currency: "INR",
        planAmount: "50000",
        recurringMonthlyAmount: "",
        perMinuteRate: "4.75",
        includedMinutes: "10500",
        maxAgents: "50",
        maxCampaigns: "200",
        maxKnowledgeBases: "500",
        maxFlows: "200",
        maxPhoneNumbers: "25",
        notes: "",
        discountPercentage: "0",
        setupPhases: [],
        primaryColor: "#6366F1",
        tagline: "",
        enableFreeTrial: false,
        freeTrialDays: "14",
        freeTrialCredits: "100",
      };
    } else {
      return {
        name: "",
        email: "",
        organizationName: "",
        country,
        currency: "USD",
        planAmount: "2000",
        recurringMonthlyAmount: "",
        perMinuteRate: "0.07",
        includedMinutes: "28500",
        maxAgents: "50",
        maxCampaigns: "200",
        maxKnowledgeBases: "500",
        maxFlows: "200",
        maxPhoneNumbers: "25",
        notes: "",
        discountPercentage: "0",
        setupPhases: [],
        primaryColor: "#6366F1",
        tagline: "",
        enableFreeTrial: false,
        freeTrialDays: "14",
        freeTrialCredits: "100",
      };
    }
  };

  const [formData, setFormData] = useState<EnterpriseFormData>(getDefaultFormData("US"));

  // Fetch enterprise accounts filtered by country
  const { data: enterpriseAccounts, isLoading: isLoadingAccounts } = useQuery<EnterpriseAccount[]>({
    queryKey: ["enterprise-accounts", selectedCountry],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/admin/enterprise-accounts?country=${selectedCountry}`);
      return res.json();
    },
  });

  // Create enterprise account mutation
  const createEnterpriseMutation = useMutation({
    mutationFn: async (data: EnterpriseFormData) => {
      // Auto-calculate included minutes based on amount and rate
      const rate = parseFloat(data.perMinuteRate) || 4.75;
      const calculatedMinutes = Math.floor(parseFloat(data.planAmount) / rate);

      const res = await apiRequest("POST", "/api/admin/enterprise-onboard", {
        name: data.name,
        email: data.email,
        organizationName: data.organizationName,
        country: data.country,
        currency: data.currency,
        planAmount: parseFloat(data.planAmount),
        recurringMonthlyAmount: data.recurringMonthlyAmount ? parseFloat(data.recurringMonthlyAmount) : null,
        perMinuteRate: parseFloat(data.perMinuteRate),
        includedMinutes: calculatedMinutes,
        maxAgents: parseInt(data.maxAgents, 10),
        maxCampaigns: parseInt(data.maxCampaigns, 10),
        maxKnowledgeBases: parseInt(data.maxKnowledgeBases, 10),
        maxFlows: parseInt(data.maxFlows, 10),
        maxPhoneNumbers: parseInt(data.maxPhoneNumbers, 10),
        notes: data.notes,
        discountPercentage: parseInt(data.discountPercentage, 10) || 0,
        primaryColor: data.primaryColor || undefined,
        tagline: data.tagline || undefined,
        enableFreeTrial: data.enableFreeTrial,
        freeTrialDays: parseInt(data.freeTrialDays, 10) || 14,
        freeTrialCredits: parseInt(data.freeTrialCredits, 10) || 100,
        setupPhases: data.setupPhases.map(phase => ({
          phaseName: phase.phaseName,
          description: phase.description,
          amount: phase.amount,
          isRequired: phase.isRequired,
        })),
      });
      return res.json() as Promise<EnterpriseOnboardResult>;
    },
    onSuccess: (result) => {
      setCreatedResult(result);
      setShowCreateDialog(false);
      setShowResultDialog(true);
      setFormData(getDefaultFormData(selectedCountry));
      queryClient.invalidateQueries({ queryKey: ["enterprise-accounts", selectedCountry] });
      toast({
        title: result.isFreeTrial ? "Free Trial Account Created" : "Enterprise Account Created",
        description: result.isFreeTrial
          ? `${result.user.email} can now login and start using the platform`
          : `Payment link sent to ${result.user.email}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Resend payment link mutation
  const resendPaymentLinkMutation = useMutation({
    mutationFn: async (accountId: string) => {
      const res = await apiRequest("POST", `/api/admin/enterprise-accounts/${accountId}/resend-payment-link`);
      return res.json();
    },
    onSuccess: (data) => {
      if (data.emailSent) {
        toast({
          title: "Payment Link Sent",
          description: "A new payment link has been emailed to the customer.",
        });
      } else {
        toast({
          title: "Email Not Sent",
          description: "Payment link generated but email could not be sent.",
          variant: "destructive",
        });
      }
      queryClient.invalidateQueries({ queryKey: ["enterprise-accounts"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validations
    if (!formData.name.trim()) {
      toast({ title: "Validation Error", description: "Full name is required", variant: "destructive" });
      return;
    }
    if (!formData.organizationName.trim()) {
      toast({ title: "Validation Error", description: "Organization name is required", variant: "destructive" });
      return;
    }
    if (!formData.email.trim()) {
      toast({ title: "Validation Error", description: "Email is required", variant: "destructive" });
      return;
    }

    // Skip amount validation for free trial accounts
    if (!formData.enableFreeTrial) {
      const amount = parseFloat(formData.planAmount);
      const minAmount = formData.currency === "INR" ? MIN_ENTERPRISE_AMOUNT_IN : MIN_ENTERPRISE_AMOUNT_US;
      const currencySymbol = formData.currency === "INR" ? "₹" : "$";
      if (isNaN(amount) || amount < minAmount) {
        toast({
          title: "Validation Error",
          description: `Minimum enterprise plan amount is ${currencySymbol}${minAmount.toLocaleString()}`,
          variant: "destructive"
        });
        return;
      }
    }

    createEnterpriseMutation.mutate(formData);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied", description: "Payment link copied to clipboard" });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "paid":
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">Paid</Badge>;
      case "pending":
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">Pending</Badge>;
      case "expired":
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">Expired</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Calculate estimated minutes based on amount
  const calculateEstimatedMinutes = (amount: string, rate: string) => {
    const amountNum = parseFloat(amount) || 0;
    const rateNum = parseFloat(rate) || 4.75;
    if (rateNum <= 0) return 0;
    return Math.floor(amountNum / rateNum);
  };

  const pricingTiers = selectedCountry === "IN" ? PRICING_TIERS_IN : PRICING_TIERS_US;
  const currencySymbol = selectedCountry === "IN" ? "₹" : "$";
  const minEnterprise = selectedCountry === "IN" ? MIN_ENTERPRISE_AMOUNT_IN : MIN_ENTERPRISE_AMOUNT_US;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-40">
            <Select
              value={selectedCountry}
              onValueChange={(value) => {
                setSelectedCountry(value);
                setFormData(getDefaultFormData(value));
              }}
            >
              <SelectTrigger data-testid="select-country-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="US">United States</SelectItem>
                <SelectItem value="IN">India</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <h3 className="text-lg font-semibold">Enterprise Onboarding</h3>
            <p className="text-sm text-muted-foreground">
              Onboard enterprise customers with custom plans and payment links
            </p>
          </div>
        </div>
        <Button onClick={() => {
          setFormData(getDefaultFormData(selectedCountry));
          setShowCreateDialog(true);
        }} className="flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          Onboard Enterprise
        </Button>
      </div>

      {/* How It Works */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">How Enterprise Onboarding Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="flex flex-col gap-2 rounded-lg border p-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-sm">1</div>
              <h4 className="font-medium">Create Account</h4>
              <p className="text-sm text-muted-foreground">
                Fill enterprise details with custom pricing. Account created as inactive.
              </p>
            </div>
            <div className="flex flex-col gap-2 rounded-lg border p-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-sm">2</div>
              <h4 className="font-medium">Payment Link Sent</h4>
              <p className="text-sm text-muted-foreground">
                Stripe payment link emailed to customer for the custom amount.
              </p>
            </div>
            <div className="flex flex-col gap-2 rounded-lg border p-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-sm">3</div>
              <h4 className="font-medium">Customer Pays</h4>
              <p className="text-sm text-muted-foreground">
                Customer completes payment via Stripe checkout page.
              </p>
            </div>
            <div className="flex flex-col gap-2 rounded-lg border p-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-green-700 font-bold text-sm">4</div>
              <h4 className="font-medium">Account Activated</h4>
              <p className="text-sm text-muted-foreground">
                On payment, account is activated with login credentials sent via email.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Enterprise Accounts List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Enterprise Accounts</CardTitle>
          <CardDescription>
            Track enterprise onboarding and payment status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingAccounts ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : !enterpriseAccounts || enterpriseAccounts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No enterprise accounts yet. Click "Onboard Enterprise" to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Plan Amount</TableHead>
                  <TableHead>Rate/Min</TableHead>
                  <TableHead>Minutes</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enterpriseAccounts.map((account) => {
                  const acctSymbol = account.currency === "INR" ? "₹" : "$";
                  return (
                  <TableRow
                    key={account.id}
                    className={account.paymentStatus === "paid" ? "bg-green-50/50 dark:bg-green-950/20" : ""}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {/* Payment status indicator - green dot for paid */}
                        <span
                          className={`inline-block w-3 h-3 rounded-full flex-shrink-0 ${
                            account.paymentStatus === "paid"
                              ? "bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]"
                              : account.paymentStatus === "pending"
                              ? "bg-yellow-500 animate-pulse"
                              : "bg-red-500"
                          }`}
                          title={account.paymentStatus === "paid" ? "Payment Complete" : account.paymentStatus === "pending" ? "Payment Pending" : "Payment Expired"}
                        />
                        {account.organizationName}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>{account.userName}</div>
                      <div className="text-sm text-muted-foreground">{account.userEmail}</div>
                    </TableCell>
                    <TableCell>
                      {account.discountPercentage > 0 ? (
                        <div>
                          <span className="text-green-600 font-medium">{acctSymbol}{parseFloat(account.planAmount).toLocaleString()}</span>
                          <div className="text-xs text-green-600">
                            {account.discountPercentage}% off
                            {account.originalAmount && (
                              <span className="text-muted-foreground line-through ml-1">
                                {acctSymbol}{parseFloat(account.originalAmount).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <>{acctSymbol}{parseFloat(account.planAmount).toLocaleString()}</>
                      )}
                    </TableCell>
                    <TableCell>{acctSymbol}{account.perMinuteRate}</TableCell>
                    <TableCell>{account.includedMinutes.toLocaleString()}</TableCell>
                    <TableCell>{getStatusBadge(account.paymentStatus)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(account.createdAt, 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {account.paymentStatus === "pending" && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(account.paymentLink)}
                              title="Copy payment link"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => resendPaymentLinkMutation.mutate(account.id)}
                              disabled={resendPaymentLinkMutation.isPending}
                              title="Resend payment link"
                            >
                              {resendPaymentLinkMutation.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <RefreshCw className="h-4 w-4" />
                              )}
                            </Button>
                          </>
                        )}
                        {account.paymentStatus === "paid" && (
                          <Badge variant="outline" className="text-green-600">
                            <Check className="h-3 w-3 mr-1" />
                            Active
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Enterprise Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Onboard Enterprise Customer
            </DialogTitle>
            <DialogDescription>
              Create an enterprise account with custom pricing. Payment link will be sent via email.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Customer Details */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Customer Details</h4>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ent-name">Contact Name</Label>
                  <Input
                    id="ent-name"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ent-email">Email</Label>
                  <Input
                    id="ent-email"
                    type="email"
                    placeholder="john@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ent-org">Organization Name</Label>
                  <Input
                    id="ent-org"
                    placeholder="Acme Corporation"
                    value={formData.organizationName}
                    onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Subdomain: <code className="bg-muted px-1 rounded">{formData.organizationName ? formData.organizationName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') : 'company'}.{TENANT_BASE_DOMAIN}</code>
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ent-country">Country</Label>
                  <Select
                    value={formData.country}
                    onValueChange={(value) => {
                      const newCurrency = value === "IN" ? "INR" : "USD";
                      const isINR = newCurrency === "INR";
                      setFormData({
                        ...formData,
                        country: value,
                        currency: newCurrency,
                        planAmount: isINR ? "50000" : "2000",
                        perMinuteRate: isINR ? "4.75" : "0.07",
                        recurringMonthlyAmount: "",
                      });
                    }}
                  >
                    <SelectTrigger id="ent-country">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="IN">India (INR ₹)</SelectItem>
                      <SelectItem value="US">United States (USD $)</SelectItem>
                      <SelectItem value="GB">United Kingdom (USD $)</SelectItem>
                      <SelectItem value="AE">UAE (USD $)</SelectItem>
                      <SelectItem value="SG">Singapore (USD $)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Tenant Branding */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ent-color">Brand Color</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="ent-color"
                      type="color"
                      value={formData.primaryColor}
                      onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={formData.primaryColor}
                      onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                      placeholder="#6366F1"
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ent-tagline">Tagline (Optional)</Label>
                  <Input
                    id="ent-tagline"
                    placeholder="Your company tagline"
                    value={formData.tagline}
                    onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Free Trial Toggle */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Gift className="h-5 w-5 text-green-600" />
                  <div>
                    <h4 className="font-medium">Start with Free Trial</h4>
                    <p className="text-sm text-muted-foreground">
                      Let customer try the platform before payment
                    </p>
                  </div>
                </div>
                <Switch
                  checked={formData.enableFreeTrial}
                  onCheckedChange={(checked) => setFormData({ ...formData, enableFreeTrial: checked })}
                />
              </div>

              {formData.enableFreeTrial && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="space-y-2">
                    <Label htmlFor="ent-trial-days">Trial Duration (Days)</Label>
                    <Input
                      id="ent-trial-days"
                      type="number"
                      min="1"
                      max="90"
                      value={formData.freeTrialDays}
                      onChange={(e) => setFormData({ ...formData, freeTrialDays: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ent-trial-credits">Trial Credits (Minutes)</Label>
                    <Input
                      id="ent-trial-credits"
                      type="number"
                      min="0"
                      value={formData.freeTrialCredits}
                      onChange={(e) => setFormData({ ...formData, freeTrialCredits: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <Alert className="bg-green-100 dark:bg-green-900 border-green-300 dark:border-green-700">
                      <Gift className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-800 dark:text-green-200">
                        Customer will get <strong>{formData.freeTrialDays} days</strong> free access with <strong>{formData.freeTrialCredits} minutes</strong> of credits.
                        No payment required until trial ends.
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>
              )}
            </div>

            {/* Pricing Configuration - Hidden during free trial */}
            {!formData.enableFreeTrial && (
            <>
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                Pricing Configuration <span className="text-xs normal-case font-normal">({formData.currency === "INR" ? "₹ INR" : "$ USD"})</span>
              </h4>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ent-amount">Initial Payment ({formData.currency === "INR" ? "₹" : "$"})</Label>
                  <Input
                    id="ent-amount"
                    type="number"
                    min={formData.country === "IN" ? MIN_ENTERPRISE_AMOUNT_IN : MIN_ENTERPRISE_AMOUNT_US}
                    placeholder={formData.country === "IN" ? MIN_ENTERPRISE_AMOUNT_IN.toLocaleString() : MIN_ENTERPRISE_AMOUNT_US.toLocaleString()}
                    value={formData.planAmount}
                    onChange={(e) => setFormData({ ...formData, planAmount: e.target.value })}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    One-time setup/first payment. Min: {formData.currency === "INR" ? "₹" : "$"}{(formData.currency === "INR" ? MIN_ENTERPRISE_AMOUNT_IN : MIN_ENTERPRISE_AMOUNT_US).toLocaleString()}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ent-recurring" className="flex items-center gap-2">
                    <RefreshCw className="h-4 w-4" />
                    Monthly Subscription ({formData.currency === "INR" ? "₹" : "$"})
                  </Label>
                  <Input
                    id="ent-recurring"
                    type="number"
                    min="0"
                    placeholder="0 (optional)"
                    value={formData.recurringMonthlyAmount}
                    onChange={(e) => setFormData({ ...formData, recurringMonthlyAmount: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    {formData.recurringMonthlyAmount && parseFloat(formData.recurringMonthlyAmount) > 0
                      ? "Recurring monthly charge after initial payment"
                      : "Leave empty for one-time payment only"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ent-rate">Per Minute Rate ({formData.currency === "INR" ? "₹" : "$"})</Label>
                  <Input
                    id="ent-rate"
                    type="number"
                    step="0.01"
                    min="1"
                    placeholder="4.75"
                    value={formData.perMinuteRate}
                    onChange={(e) => setFormData({ ...formData, perMinuteRate: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Included Minutes (Auto-calculated)</Label>
                  <div className="h-10 px-3 py-2 rounded-md border bg-muted/50 flex items-center">
                    <span className="font-medium text-lg">
                      {formData.recurringMonthlyAmount && parseFloat(formData.recurringMonthlyAmount) > 0
                        ? calculateEstimatedMinutes(formData.recurringMonthlyAmount, formData.perMinuteRate).toLocaleString()
                        : calculateEstimatedMinutes(formData.planAmount, formData.perMinuteRate).toLocaleString()}
                    </span>
                    <span className="text-muted-foreground ml-1">minutes</span>
                    {formData.recurringMonthlyAmount && parseFloat(formData.recurringMonthlyAmount) > 0 && (
                      <span className="text-muted-foreground ml-1">/month</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formData.recurringMonthlyAmount && parseFloat(formData.recurringMonthlyAmount) > 0
                      ? `${formData.currency === "INR" ? "₹" : "$"}${parseFloat(formData.recurringMonthlyAmount || "0").toLocaleString()} ÷ ${formData.currency === "INR" ? "₹" : "$"}${formData.perMinuteRate}/min`
                      : `${formData.currency === "INR" ? "₹" : "$"}${parseFloat(formData.planAmount || "0").toLocaleString()} ÷ ${formData.currency === "INR" ? "₹" : "$"}${formData.perMinuteRate}/min`}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ent-discount" className="flex items-center gap-2">
                  <Percent className="h-4 w-4" />
                  Discount %
                </Label>
                <Input
                  id="ent-discount"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="0"
                  value={formData.discountPercentage}
                  onChange={(e) => setFormData({ ...formData, discountPercentage: e.target.value })}
                  className="max-w-[200px]"
                />
                {parseInt(formData.discountPercentage || "0") > 0 && (
                  <p className="text-xs text-green-600">
                    Discount applies to {formData.recurringMonthlyAmount && parseFloat(formData.recurringMonthlyAmount) > 0 ? "both initial and monthly payments" : "initial payment"}
                  </p>
                )}
              </div>
            </div>

            {/* Resource Limits */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Resource Limits</h4>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ent-agents">Max Agents</Label>
                  <Input
                    id="ent-agents"
                    type="number"
                    min="1"
                    value={formData.maxAgents}
                    onChange={(e) => setFormData({ ...formData, maxAgents: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ent-campaigns">Max Campaigns</Label>
                  <Input
                    id="ent-campaigns"
                    type="number"
                    min="1"
                    value={formData.maxCampaigns}
                    onChange={(e) => setFormData({ ...formData, maxCampaigns: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ent-kb">Max Knowledge Bases</Label>
                  <Input
                    id="ent-kb"
                    type="number"
                    min="1"
                    value={formData.maxKnowledgeBases}
                    onChange={(e) => setFormData({ ...formData, maxKnowledgeBases: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ent-flows">Max Flows</Label>
                  <Input
                    id="ent-flows"
                    type="number"
                    min="1"
                    value={formData.maxFlows}
                    onChange={(e) => setFormData({ ...formData, maxFlows: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ent-phones">Max Phone Numbers</Label>
                  <Input
                    id="ent-phones"
                    type="number"
                    min="1"
                    value={formData.maxPhoneNumbers}
                    onChange={(e) => setFormData({ ...formData, maxPhoneNumbers: e.target.value })}
                  />
                </div>
              </div>
            </div>
            </>
            )}

            {/* Notes */}
            <div className="space-y-2 pt-4 border-t">
              <Label htmlFor="ent-notes">Internal Notes (Optional)</Label>
              <Textarea
                id="ent-notes"
                placeholder="Any special requirements or notes about this enterprise deal..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>

            {/* Setup/Development Phases - Hidden during free trial */}
            {!formData.enableFreeTrial && (
            <div className="pt-4 border-t">
              <SetupPhasesManager
                phases={formData.setupPhases}
                onChange={(phases) => setFormData({ ...formData, setupPhases: phases })}
                currency={formData.currency}
                currencySymbol={formData.currency === "INR" ? "₹" : "$"}
              />
            </div>
            )}

            {/* Summary */}
            <div className={`rounded-lg border p-4 ${formData.enableFreeTrial ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950' : 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950'}`}>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                {formData.enableFreeTrial && <Gift className="h-4 w-4 text-green-600" />}
                Summary
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {formData.enableFreeTrial ? (
                  <>
                    <div className="text-muted-foreground">Trial Duration:</div>
                    <div className="font-medium text-green-600">{formData.freeTrialDays} days</div>
                    <div className="text-muted-foreground">Trial Credits:</div>
                    <div className="font-medium text-green-600">{formData.freeTrialCredits} minutes</div>
                    <div className="text-muted-foreground">Initial Payment:</div>
                    <div className="font-medium text-green-600">Free (No payment required)</div>
                  </>
                ) : (
                  <>
                    <div className="text-muted-foreground">Initial Payment:</div>
                    <div className="font-medium">
                      {parseInt(formData.discountPercentage || "0") > 0 ? (
                        <>
                          <span className="line-through text-muted-foreground mr-2">
                            {formData.currency === "INR" ? "₹" : "$"}{parseFloat(formData.planAmount || "0").toLocaleString()}
                          </span>
                          <span className="text-green-600">
                            {formData.currency === "INR" ? "₹" : "$"}
                            {Math.round(parseFloat(formData.planAmount || "0") * (1 - parseInt(formData.discountPercentage || "0") / 100)).toLocaleString()}
                          </span>
                        </>
                      ) : (
                        <>{formData.currency === "INR" ? "₹" : "$"}{parseFloat(formData.planAmount || "0").toLocaleString()}</>
                      )}
                    </div>
                  </>
                )}
                {!formData.enableFreeTrial && formData.recurringMonthlyAmount && parseFloat(formData.recurringMonthlyAmount) > 0 && (
                  <>
                    <div className="text-muted-foreground">Monthly Subscription:</div>
                    <div className="font-medium text-primary">
                      {parseInt(formData.discountPercentage || "0") > 0 ? (
                        <>
                          <span className="line-through text-muted-foreground mr-2">
                            {formData.currency === "INR" ? "₹" : "$"}{parseFloat(formData.recurringMonthlyAmount).toLocaleString()}
                          </span>
                          <span className="text-green-600">
                            {formData.currency === "INR" ? "₹" : "$"}
                            {Math.round(parseFloat(formData.recurringMonthlyAmount) * (1 - parseInt(formData.discountPercentage || "0") / 100)).toLocaleString()}/mo
                          </span>
                        </>
                      ) : (
                        <>{formData.currency === "INR" ? "₹" : "$"}{parseFloat(formData.recurringMonthlyAmount).toLocaleString()}/mo</>
                      )}
                    </div>
                  </>
                )}
                {!formData.enableFreeTrial && parseInt(formData.discountPercentage || "0") > 0 && (
                  <>
                    <div className="text-muted-foreground">Discount:</div>
                    <div className="font-medium text-green-600">{formData.discountPercentage}% off</div>
                  </>
                )}
                {!formData.enableFreeTrial && (
                  <>
                    <div className="text-muted-foreground">Per Minute:</div>
                    <div className="font-medium">{formData.currency === "INR" ? "₹" : "$"}{formData.perMinuteRate}</div>
                    <div className="text-muted-foreground">Included Minutes:</div>
                    <div className="font-medium">
                      {formData.recurringMonthlyAmount && parseFloat(formData.recurringMonthlyAmount) > 0
                        ? calculateEstimatedMinutes(formData.recurringMonthlyAmount, formData.perMinuteRate).toLocaleString()
                        : calculateEstimatedMinutes(formData.planAmount, formData.perMinuteRate).toLocaleString()}
                      {formData.recurringMonthlyAmount && parseFloat(formData.recurringMonthlyAmount) > 0 && <span className="text-muted-foreground">/mo</span>}
                    </div>
                  </>
                )}
                {!formData.enableFreeTrial && formData.setupPhases.length > 0 && (
                  <>
                    <div className="text-muted-foreground">Setup Phases:</div>
                    <div className="font-medium text-amber-600">
                      {formData.setupPhases.length} phase(s) - {formData.currency === "INR" ? "₹" : "$"}
                      {formData.setupPhases.reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString()}
                    </div>
                  </>
                )}
              </div>
              <p className={`text-xs mt-3 ${formData.enableFreeTrial ? 'text-green-700 dark:text-green-300' : 'text-blue-700 dark:text-blue-300'}`}>
                {formData.enableFreeTrial ? (
                  <>
                    <Gift className="inline h-3 w-3 mr-1" />
                    Account will be <strong>active immediately</strong>. Customer can login and start using the platform right away.
                  </>
                ) : (
                  <>
                    <Clock className="inline h-3 w-3 mr-1" />
                    {formData.recurringMonthlyAmount && parseFloat(formData.recurringMonthlyAmount) > 0 ? (
                      <>Initial payment collected first. Monthly subscription starts automatically after.</>
                    ) : formData.setupPhases.filter(p => p.isRequired).length > 0 ? (
                      <>Account activates after all required setup phases are paid.</>
                    ) : (
                      <>Account will be created as <strong>inactive</strong>. User can only login after payment is completed.</>
                    )}
                  </>
                )}
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createEnterpriseMutation.isPending}
                className="flex items-center gap-2"
              >
                {createEnterpriseMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : formData.enableFreeTrial ? (
                  <>
                    <Gift className="h-4 w-4" />
                    Create Free Trial Account
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4" />
                    Create & Send Payment Link
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Result Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              {createdResult?.isFreeTrial ? <Gift className="h-5 w-5" /> : <Check className="h-5 w-5" />}
              {createdResult?.isFreeTrial ? "Free Trial Account Created" : "Enterprise Account Created"}
            </DialogTitle>
            <DialogDescription>
              {createdResult?.isFreeTrial
                ? "The trial account is active and ready to use."
                : "The account has been created and payment link generated."}
            </DialogDescription>
          </DialogHeader>

          {createdResult && (
            <div className="space-y-4 py-4">
              <div className="rounded-lg border p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Organization</span>
                  <span className="font-medium">{createdResult.organization.name}</span>
                </div>
                {createdResult.organization.subdomain && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Subdomain</span>
                    <a
                      href={`https://${createdResult.organization.subdomain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      {createdResult.organization.subdomain}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Contact</span>
                  <span className="font-medium">{createdResult.user.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Email</span>
                  <span className="font-medium">{createdResult.user.email}</span>
                </div>
                {createdResult.isFreeTrial ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Trial Expires</span>
                      <span className="font-medium">{formatDate(createdResult.trialExpiresAt, 'MMM d, yyyy')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Trial Credits</span>
                      <span className="font-medium">{createdResult.trialCredits} minutes</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Status</span>
                      <Badge variant="outline" className="text-green-600 bg-green-50">
                        <Check className="h-3 w-3 mr-1" />
                        Active (Trial)
                      </Badge>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Status</span>
                    <Badge variant="outline" className="text-yellow-600">
                      <Clock className="h-3 w-3 mr-1" />
                      Pending Payment
                    </Badge>
                  </div>
                )}
              </div>

              {/* Temporary Password for Free Trial */}
              {createdResult.isFreeTrial && createdResult.temporaryPassword && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Key className="h-4 w-4" />
                    Temporary Password
                  </Label>
                  <div className="flex gap-2">
                    <Input value={createdResult.temporaryPassword} readOnly className="font-mono" />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(createdResult.temporaryPassword!)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Share this password with the customer. They will be asked to change it on first login.
                  </p>
                </div>
              )}

              {/* Payment Link - Only for paid accounts */}
              {!createdResult.isFreeTrial && createdResult.paymentLink && (
                <div className="space-y-2">
                  <Label>Payment Link</Label>
                  <div className="flex gap-2">
                    <Input value={createdResult.paymentLink} readOnly className="text-xs" />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(createdResult.paymentLink!)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => window.open(createdResult.paymentLink, "_blank")}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Email Status Alert */}
              {createdResult.emailSent ? (
                <Alert className={createdResult.isFreeTrial ? "bg-green-50 border-green-200" : ""}>
                  <Mail className="h-4 w-4 text-green-600" />
                  <AlertDescription>
                    {createdResult.isFreeTrial ? (
                      <>Welcome email with login credentials has been sent to <strong>{createdResult.user.email}</strong>.</>
                    ) : (
                      <>Payment link has been emailed to <strong>{createdResult.user.email}</strong>.
                      The customer will receive login credentials after completing payment.</>
                    )}
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription>
                    <p className="font-medium mb-1 text-amber-700 dark:text-amber-300">
                      Email was not sent.{createdResult.emailError ? ` Reason: ${createdResult.emailError}` : ' SMTP may not be configured.'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {createdResult.isFreeTrial
                        ? "Please share the login credentials manually with the customer."
                        : "Please share the payment link manually with the customer."}
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {/* DNS Status Alert */}
              {createdResult.dnsProvisioned !== undefined && (
                createdResult.dnsProvisioned ? (
                  <Alert className="bg-blue-50 border-blue-200">
                    <ExternalLink className="h-4 w-4 text-blue-600" />
                    <AlertDescription>
                      DNS record automatically provisioned. The subdomain <strong>{createdResult.organization.subdomain}</strong> is now active.
                    </AlertDescription>
                  </Alert>
                ) : createdResult.dnsError ? (
                  <Alert>
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription>
                      <p className="font-medium mb-1 text-amber-700 dark:text-amber-300">
                        DNS provisioning failed: {createdResult.dnsError}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Please configure DNS manually or check AWS Route 53 settings.
                      </p>
                    </AlertDescription>
                  </Alert>
                ) : null
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowResultDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
